/**
 * Android Play update checks via Google Play In-App Updates
 * (`@capawesome/capacitor-app-update`). When that API reports nothing (typical
 * on internal testing tracks), compare against published `android-latest.json`
 * and open the Play Store *app* listing (`market://`) so testers can tap Update.
 *
 * iOS is intentionally out of scope.
 */
import { Capacitor } from '@capacitor/core'
import {
  AppUpdate,
  AppUpdateAvailability,
  FlexibleUpdateInstallStatus,
  type AppUpdateInfo,
  type AppUpdateResult,
  AppUpdateResultCode,
} from '@capawesome/capacitor-app-update'
import { env } from '../config/env'
import { openExternalUrl } from '../utils/open-external'
import { getNativePlatform } from '../utils/platform'
import {
  ANDROID_PLAY_PACKAGE_ID,
  ANDROID_PLAY_STORE_HTTPS_URL,
  isAndroidUpdateNeeded,
  maxAndroidVersionCode,
  parseAndroidVersionCode,
} from '../utils/android-store-version'

export type AndroidUpdatePrompt = {
  availableVersionCode: string
  currentVersionCode: string
  /** Flexible or immediate Play In-App Update can run. */
  canUseInAppUpdate: boolean
  /** Flexible download finished — caller should offer restart / complete. */
  readyToInstall: boolean
}

function isAndroidNative(): boolean {
  return Capacitor.isNativePlatform() && getNativePlatform() === 'android'
}

function pluginAvailable(): boolean {
  return Capacitor.isPluginAvailable('AppUpdate')
}

function toPrompt(info: AppUpdateInfo): AndroidUpdatePrompt | null {
  const availableVersionCode = info.availableVersionCode?.trim() ?? ''
  const currentVersionCode = info.currentVersionCode?.trim() ?? ''
  const readyToInstall = info.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED

  if (readyToInstall) {
    return {
      availableVersionCode: availableVersionCode || currentVersionCode,
      currentVersionCode,
      canUseInAppUpdate: true,
      readyToInstall: true,
    }
  }

  if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
    return null
  }

  // Play's UPDATE_AVAILABLE flag is not enough: the same versionCode can sit on
  // Internal testing (already installed) and Production (in review). Prompt only
  // when availableVersionCode parses and is numerically newer than current.
  if (!isAndroidUpdateNeeded(currentVersionCode, availableVersionCode)) {
    return null
  }

  return {
    availableVersionCode,
    currentVersionCode,
    canUseInAppUpdate: Boolean(info.flexibleUpdateAllowed || info.immediateUpdateAllowed),
    readyToInstall: false,
  }
}

/**
 * Open the Play Store listing for this package.
 *
 * Prefer `AppUpdate.openAppStore()` — native `market://details?id=…` intent,
 * with an HTTPS Play URL fallback inside the plugin. Do **not** open the
 * internal-test join page in Custom Tabs: that 302s to Google login and never
 * starts an update. `Browser.open` / `window.open` of http(s) is last resort.
 */
export async function openAndroidPlayUpdateUrl(): Promise<boolean> {
  if (pluginAvailable()) {
    try {
      await AppUpdate.openAppStore({ androidPackageName: ANDROID_PLAY_PACKAGE_ID })
      return true
    } catch {
      /* fall through to HTTPS */
    }
  }

  const httpsUrl = env.androidPlayUpdateUrl || ANDROID_PLAY_STORE_HTTPS_URL
  if (!httpsUrl) return false
  return openExternalUrl(httpsUrl)
}

async function openStoreOrFail(): Promise<'opened-store' | 'failed'> {
  return (await openAndroidPlayUpdateUrl()) ? 'opened-store' : 'failed'
}

const REMOTE_VERSION_FETCH_MS = 5_000

function publishedAndroidVersionUrl(): string {
  if (env.androidLatestVersionUrl) return env.androidLatestVersionUrl
  return `${env.publicAppUrl}/android-latest.json`
}

async function fetchRemotePublishedVersionCode(): Promise<number | null> {
  const url = `${publishedAndroidVersionUrl()}?t=${Date.now()}`
  try {
    const { CapacitorHttp } = await import('@capacitor/core')
    const response = await CapacitorHttp.get({
      url,
      connectTimeout: REMOTE_VERSION_FETCH_MS,
      readTimeout: REMOTE_VERSION_FETCH_MS,
    })
    if (typeof response.status === 'number' && response.status >= 400) return null
    const parsed =
      typeof response.data === 'string'
        ? (JSON.parse(response.data) as { versionCode?: unknown })
        : (response.data as { versionCode?: unknown } | null)
    return parseAndroidVersionCode(parsed?.versionCode)
  } catch {
    try {
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), REMOTE_VERSION_FETCH_MS)
      const res = await fetch(url, { cache: 'no-store', signal: controller.signal })
      clearTimeout(timer)
      if (!res.ok) return null
      const data = (await res.json()) as { versionCode?: unknown }
      return parseAndroidVersionCode(data?.versionCode)
    } catch {
      return null
    }
  }
}

async function resolvePublishedAndroidVersionCode(): Promise<number | null> {
  const fromEnv = parseAndroidVersionCode(env.androidLatestVersionCode)
  const fromRemote = await fetchRemotePublishedVersionCode()
  return maxAndroidVersionCode(fromEnv, fromRemote)
}

async function readRunningVersionCode(): Promise<string> {
  try {
    const { App } = await import('@capacitor/app')
    const info = await App.getInfo()
    return (info.build ?? '').trim()
  } catch {
    return ''
  }
}

function storeLinkPrompt(currentVersionCode: string, storeVersionCode: number): AndroidUpdatePrompt {
  return {
    availableVersionCode: String(storeVersionCode),
    currentVersionCode,
    canUseInAppUpdate: false,
    readyToInstall: false,
  }
}

/**
 * Query Play for an available update. When Play reports nothing — common on
 * internal testing tracks, sideloads, and missing plugins — compare the
 * running versionCode to a published store version (remote JSON on the
 * public app origin, plus optional VITE_ANDROID_LATEST_VERSION_CODE) and
 * show our own prompt that opens the Play Store app listing.
 *
 * Always compare numeric versionCode. Never treat marketing versionName
 * (`1.0.0`) as a store version. Do not prompt when local >= latest — Play's
 * UPDATE_AVAILABLE flag can fire while Production is still in review and the
 * Internal listing only offers Open.
 *
 * There is no CapacitorUpdater / live-update path; native web assets are bundled.
 */
export async function checkAndroidUpdateAvailability(): Promise<AndroidUpdatePrompt | null> {
  if (!isAndroidNative()) return null

  let playPrompt: AndroidUpdatePrompt | null = null
  let currentVersionCode = ''

  if (pluginAvailable()) {
    try {
      const info = await AppUpdate.getAppUpdateInfo()
      currentVersionCode = info.currentVersionCode?.trim() ?? ''
      playPrompt = toPrompt(info)
    } catch {
      playPrompt = null
    }
  }

  if (playPrompt) return playPrompt

  if (parseAndroidVersionCode(currentVersionCode) == null) {
    currentVersionCode = await readRunningVersionCode()
  }

  const published = await resolvePublishedAndroidVersionCode()
  if (published != null && isAndroidUpdateNeeded(currentVersionCode, published)) {
    return storeLinkPrompt(currentVersionCode, published)
  }
  return null
}

async function startFlexibleAndWaitResult(): Promise<AppUpdateResult> {
  return AppUpdate.startFlexibleUpdate()
}

/**
 * Soft update action:
 * 1. Complete a finished flexible download when ready
 * 2. Prefer flexible in-app update, then immediate, when Play said that is allowed
 * 3. Otherwise open the Play Store app listing (`market://`, then HTTPS)
 */
export async function startAndroidUpdate(prompt: AndroidUpdatePrompt): Promise<'completed' | 'started' | 'opened-store' | 'cancelled' | 'failed'> {
  if (!isAndroidNative()) {
    return openStoreOrFail()
  }

  if (prompt.readyToInstall && pluginAvailable()) {
    try {
      await AppUpdate.completeFlexibleUpdate()
      return 'completed'
    } catch {
      return openStoreOrFail()
    }
  }

  // JSON-fallback / sideload prompt: Play already said in-app updates cannot
  // run. Skip a second getAppUpdateInfo round-trip and open the store listing.
  if (!prompt.canUseInAppUpdate || !pluginAvailable()) {
    return openStoreOrFail()
  }

  try {
    // Refresh info right before starting — Play requires a recent getAppUpdateInfo.
    const info = await AppUpdate.getAppUpdateInfo()
    const fresh = toPrompt(info)
    if (!fresh) {
      return openStoreOrFail()
    }
    if (fresh.readyToInstall) {
      await AppUpdate.completeFlexibleUpdate()
      return 'completed'
    }

    if (info.flexibleUpdateAllowed) {
      const result = await startFlexibleAndWaitResult()
      if (result.code === AppUpdateResultCode.OK) return 'started'
      if (result.code === AppUpdateResultCode.CANCELED) return 'cancelled'
      // Fall through to store on other codes.
    } else if (info.immediateUpdateAllowed) {
      const result = await AppUpdate.performImmediateUpdate()
      if (result.code === AppUpdateResultCode.OK) return 'completed'
      if (result.code === AppUpdateResultCode.CANCELED) return 'cancelled'
    }

    return openStoreOrFail()
  } catch {
    return openStoreOrFail()
  }
}

/** Subscribe to flexible download progress; returns an unsubscribe handle. */
export async function watchFlexibleUpdateDownloaded(
  onDownloaded: () => void,
): Promise<() => void> {
  if (!isAndroidNative() || !pluginAvailable()) return () => {}
  try {
    const handle = await AppUpdate.addListener('onFlexibleUpdateStateChange', (state) => {
      if (state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
        onDownloaded()
      }
    })
    return () => {
      void handle.remove()
    }
  } catch {
    return () => {}
  }
}
